import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  Input,
  Output,
  EventEmitter,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { MediaService } from '../../../shared/services/media.service';

@Component({
  selector: 'app-file-upload',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: FileUploadComponent,
      multi: true,
    },
  ],
})
export class FileUploadComponent implements ControlValueAccessor {
  onChange: (file: File | null) => {};
  @Output() delete: EventEmitter<any> = new EventEmitter();
  @Output() upload: EventEmitter<any> = new EventEmitter();
  @Output() startUpload: EventEmitter<any> = new EventEmitter();
  @Output() finished: EventEmitter<any> = new EventEmitter();
  @Output() fail: EventEmitter<any> = new EventEmitter();
  @Input() preview: string | ArrayBuffer | SafeUrl;
  @Input() id: number;
  @Input() isDeletable = true;
  @Input() accept;
  @Input() clearAfterUpload = false;
  file: File | null = null;

  constructor(
    private host: ElementRef<HTMLInputElement>,
    private toastrService: ToastrService,
    private sanitizer: DomSanitizer,
    private mediaService: MediaService,
  ) {}
  @ViewChild('fileInput') fileInput: ElementRef;
  @HostListener('change', ['$event.target.files']) emitFiles(event: FileList) {
    const file = event?.item(0);
    this.file = file;
    this.startUpload.emit('started');
    if (!this.file) return;

    if (this.accept && !this.isFileExtensionAllowed(this.file)) {
      this.toastrService.error(
        `Only files with the following extensions are allowed: ${this.accept}`,
      );
      this.fail.emit();
      this.clearInput();
      return;
    }

    this.mediaService.uploadMedia({ file: this.file }).subscribe({
      next: (res) => {
        const resWithSize = {
          ...res,
          fileSize: this.file?.size || 0,
        };
        this.upload.emit(resWithSize);
        this.id = res.id;
        if (this.clearAfterUpload) {
          this.clear();
        }
      },
      error: () => {
        this.fail.emit();
      },
      complete: () => {
        this.finished.emit('finished');
      },
    });

    if (this.file) {
      const mimeType = file.type;

      if (/^image\/.*/.test(mimeType)) {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          this.preview = reader.result;
        };
      }
    }
  }
  clickInput() {
    this.fileInput.nativeElement.click();
  }
  clickFileInput() {
    this.fileInput.nativeElement.click();
  }
  writeValue(value: null) {
    this.preview = value;
    this.host.nativeElement.value = '';
    this.file = null;
  }

  registerOnChange(fn) {
    this.onChange = fn;
  }

  registerOnTouched(fn) {
    console.log(fn);
  }

  clear() {
    this.file = null;
    this.preview = null;
    this.delete.emit(this.id);
    this.id = null;

    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  getImageUrl(url) {
    return this.sanitizer.bypassSecurityTrustUrl(url as string);
  }

  private isFileExtensionAllowed(file: File): boolean {
    if (!this.accept) return true;

    const fileName = file.name.toLowerCase();
    const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
    const allowedExtensions = this.accept
      .split(',')
      .map((ext) => ext.trim().toLowerCase());

    return allowedExtensions.some((ext) => {
      const normalizedExt = ext.toLowerCase().startsWith('.')
        ? ext.toLowerCase()
        : `.${ext.toLowerCase()}`;
      return fileExtension === normalizedExt;
    });
  }

  private clearInput() {
    this.file = null;
    this.preview = null;
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
    if (this.host?.nativeElement) {
      this.host.nativeElement.value = '';
    }
  }
}
